import "./deputy-modal.scss";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModalBackground } from "../../components/solicitor/shared/modal-background";
import RadioGroup from "../../components/form/grouped/radio/radio-group";
import { RadioItemInterface } from "../../models/interfaces/radio-item.interface";
import { useGetCoveredRolesQuery } from "../../redux/services/getCoveredRoles";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { useGetApplicationConsentByUuidQuery } from "../../redux/services/getApplicationConsent";
import { PolymerCrypto } from "../../utils/encryptor";
import { P2Button, P2ButtonGroup } from "react-bootstrap-common-script";
import { useNavigate } from "react-router-dom";
import { updateLoginUser } from "../../redux/slices/authentication-slice";

export interface DeputyModalHandle {
  setIsShowing: (isShowing: boolean) => void;
}

const DeputyModal = forwardRef<DeputyModalHandle, {}>(function DeputyModal(_, ref) {
  // States
  const { loginUser, originalLoginUser, singpassToken } = useAppSelector((state) => state.auth.AuthenticationSlice);
  const owners = useAppSelector((state) => state.app.ApplicationStartSlice.formData.selected_hdb_property?.owners) ?? [];
  const { applicationId } = useAppSelector((state) => state.app.ApplicationIdSlice);
  const { data: coveredRolesData, isLoading: coveredRolesIsLoading } = useGetCoveredRolesQuery({
    singpass_token: singpassToken ?? "",
    roleNrics: [originalLoginUser.uinfin],
  });
  const { data: consentData, isLoading: consentIsLoading } = useGetApplicationConsentByUuidQuery(
    { applicationUuid: applicationId ?? "0", uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "" },
    { skip: applicationId === "0" || applicationId === null }
  );
  const [isShowing, setIsShowing] = useState<boolean>(false);
  const [selectedUinfin, setSelectedUinfin] = useState<string>("");

  // Refs
  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Utils
  const navigate = useNavigate();
  const AppDispatch = useAppDispatch();

  // Derived data
  const radioOptions: RadioItemInterface[] = useMemo(() => {
    if (!consentData) {
      return [];
    }

    const pendingOwners = consentData.owners_consent_status
      .filter((status) => status.consent_check === "MUST_CHECK" && !status.consent_given)
      .map((status) => ({
        ...status,
        uin_fin: PolymerCrypto.decryption(status.uin_fin),
      }));

    const representedOwners = pendingOwners.filter((owner) => coveredRolesData?.some((coveringRole) => coveringRole.owner_nric === owner.uin_fin));

    return representedOwners.map((representedOwner, index) => ({
      id: index,
      description: `Deputy-ship for ${representedOwner.owner_name}`,
      variant: "default",
      value: representedOwner.uin_fin,
    }));
  }, [consentData]);

  // Functions
  const handleNavigation = useCallback(() => {
    const representedOwner = owners.find((owner) => owner.uinfin === selectedUinfin);

    if (!representedOwner) {
      return;
    }

    AppDispatch(updateLoginUser({ ...representedOwner, uinfin: selectedUinfin }));
    navigate(`/application-coo/stage-b/review?application_id=${applicationId}`);
  }, [selectedUinfin, owners, navigate]);

  // Effects
  useEffect(() => {
    if (radioOptions.length > 0) {
      setIsShowing(true);
      setSelectedUinfin(radioOptions[0].value as string);
    }
  }, [radioOptions]);

  if (!isShowing || coveredRolesIsLoading || consentIsLoading) {
    return null;
  }

  return (
    <ModalBackground innerComponentRef={modalContainerRef} callback={() => setIsShowing(false)}>
      <div ref={modalContainerRef} className="polymer-deputy-modal__container">
        <div className="polymer-deputy-modal__title-container">
          <h2 className="polymer-deputy-modal__title">Give your consent as a deputy</h2>
        </div>
        <div className="mb-4">
        We note that you have multiple roles in this application for change in flat ownership. As the application has been successfully created, please provide your consent as a:
        </div>
        <RadioGroup
          className="mb-4"
          required={true}
          r2Enabled={true}
          gap={8}
          layout="default"
          items={radioOptions}
          value={selectedUinfin}
          disabled={false}
          onItemSelect={(e) => setSelectedUinfin(e)}
        />
        <div className="polymer-deputy-modal__button-container">
          <P2ButtonGroup>
            <P2Button variant="outline-primary" onClick={() => setIsShowing(false)}>
              No, do later
            </P2Button>
            <P2Button variant="primary-1" disabled={!selectedUinfin} onClick={handleNavigation}>
              Yes, I give consent
            </P2Button>
          </P2ButtonGroup>
        </div>
      </div>
    </ModalBackground>
  );
});

export { DeputyModal };
